/**
 * Color validation utilities for branding
 */

/**
 * Validate hex color format
 */
export function validateHexColor(color: string): boolean {
  const hexPattern = /^#[0-9A-Fa-f]{6}$/;
  return hexPattern.test(color);
}

/**
 * Convert hex color to RGB
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Calculate relative luminance of a color
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
export function relativeLuminance(rgb: { r: number; g: number; b: number }): number {
  const rsRGB = rgb.r / 255;
  const gsRGB = rgb.g / 255;
  const bsRGB = rgb.b / 255;

  const r = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const g = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const b = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculate contrast ratio between two colors
 * https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 */
export function calculateContrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) {
    throw new Error('Invalid hex color format');
  }

  const lum1 = relativeLuminance(rgb1);
  const lum2 = relativeLuminance(rgb2);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Validate WCAG AA contrast ratio (minimum 4.5:1)
 */
export function validateWCAGContrast(
  primaryColor: string,
  textColor: string
): { isValid: boolean; ratio: number; message?: string } {
  try {
    const ratio = calculateContrastRatio(primaryColor, textColor);
    const isValid = ratio >= 4.5;

    return {
      isValid,
      ratio,
      message: isValid
        ? undefined
        : `Contrast ratio ${ratio.toFixed(2)}:1 does not meet WCAG AA minimum of 4.5:1. Please adjust colors for better accessibility.`,
    };
  } catch (error) {
    return {
      isValid: false,
      ratio: 0,
      message: error instanceof Error ? error.message : 'Error calculating contrast ratio',
    };
  }
}
